import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  AttachMoney,
  CardMembership,
  Close,
  Delete,
  DriveEta,
  Edit,
  Save,
  Schedule,
  Settings,
  ToggleOff,
  ToggleOn,
  Warning,
} from "@mui/icons-material";
import apiClient from "../../utils/axios";

// ── Constantes y componente PlanForm fuera del componente padre ──────────────
// (Definirlos dentro causaría que React los desmonte/remonte en cada render,
//  perdiendo el foco al escribir)

const DIAS = [
  { value: 0, label: "L", title: "Lunes" },
  { value: 1, label: "M", title: "Martes" },
  { value: 2, label: "X", title: "Miércoles" },
  { value: 3, label: "J", title: "Jueves" },
  { value: 4, label: "V", title: "Viernes" },
  { value: 5, label: "S", title: "Sábado" },
  { value: 6, label: "D", title: "Domingo" },
];

const PLAN_VACIO = {
  nombre: "", tipo: "mensual", precio: 0,
  aplica_iva: false, porcentaje_iva: 15,
  dias_semana: [0, 1, 2, 3, 4], todo_el_dia: false,
  hora_inicio: "08:00", hora_fin: "18:00", descripcion: "",
  horas_prepago: 0,
};

const PlanForm = ({ plan, onChange }) => {
  const esTarjeta = plan.tipo === "tarjeta";
  return (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          size="small" fullWidth label="Nombre"
          value={plan.nombre}
          onChange={(e) => onChange({ ...plan, nombre: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormControl size="small" fullWidth>
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={plan.tipo}
            onChange={(e) => onChange({ ...plan, tipo: e.target.value })}
          >
            <MenuItem value="diario">Diario</MenuItem>
            <MenuItem value="mensual">Mensual</MenuItem>
            <MenuItem value="tarjeta">Tarjeta Prepago</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {esTarjeta ? (
        <Grid item xs={12} sm={3}>
          <TextField
            size="small" fullWidth label="Horas asignadas" type="number"
            inputProps={{ min: 1, step: 0.5 }}
            value={plan.horas_prepago || ""}
            onChange={(e) => onChange({ ...plan, horas_prepago: Number(e.target.value) })}
            InputProps={{
              endAdornment: <InputAdornment position="end">h</InputAdornment>,
            }}
            helperText="Se descuentan al entrar/salir"
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={3}>
          <TextField
            size="small" fullWidth label="Precio" type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={plan.precio}
            onChange={(e) => onChange({ ...plan, precio: Number(e.target.value) })}
            InputProps={{
              startAdornment: <InputAdornment position="start"><AttachMoney fontSize="small" /></InputAdornment>,
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">Días de la semana</Typography>
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
          {DIAS.map((d) => {
            const sel = plan.dias_semana.includes(d.value);
            return (
              <Tooltip key={d.value} title={d.title}>
                <Chip
                  label={d.label} size="small" clickable
                  color={sel ? "primary" : "default"}
                  variant={sel ? "filled" : "outlined"}
                  onClick={() => {
                    const dias = sel
                      ? plan.dias_semana.filter((v) => v !== d.value)
                      : [...plan.dias_semana, d.value].sort();
                    onChange({ ...plan, dias_semana: dias });
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={plan.todo_el_dia}
              onChange={(e) => onChange({ ...plan, todo_el_dia: e.target.checked })}
            />
          }
          label="Todo el día"
        />
      </Grid>
      {!plan.todo_el_dia && (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth label="Hora inicio" type="time"
              value={plan.hora_inicio || ""}
              onChange={(e) => onChange({ ...plan, hora_inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth label="Hora fin" type="time"
              value={plan.hora_fin || ""}
              onChange={(e) => onChange({ ...plan, hora_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </>
      )}
      {!esTarjeta && (
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={plan.aplica_iva}
                onChange={(e) => onChange({ ...plan, aplica_iva: e.target.checked })}
              />
            }
            label="Aplica IVA"
          />
        </Grid>
      )}
      {!esTarjeta && plan.aplica_iva && (
        <Grid item xs={12} sm={4}>
          <TextField
            size="small" fullWidth label="% IVA" type="number"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            value={plan.porcentaje_iva}
            onChange={(e) => onChange({ ...plan, porcentaje_iva: Number(e.target.value) })}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          size="small" fullWidth label="Descripción (opcional)"
          value={plan.descripcion || ""}
          onChange={(e) => onChange({ ...plan, descripcion: e.target.value })}
        />
      </Grid>
    </Grid>
  </Box>
  );
};

const PaymentSettings = ({ open, onClose, vehicles }) => {;
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    precioPorHora: 1,
    typePriceCalc: "hourly",
    fixedPrice: 0,
    lostTicketValue: 0,
    invoiceDiscountHours: 1,
    tiempoGratisHabilitado: false,
    minutosGratis: 0,
    moneda: "USD",
    minutosSalida: 15,
    totalEspacios: 0,
    espacioParqueoUsado: 0,
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const [cambiosPendientes, setCambiosPendientes] = useState(false);
  const [confirmGuardar, setConfirmGuardar] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: "", minutos_gratis: 0, precio_por_hora: null });
  const [editandoCategoria, setEditandoCategoria] = useState(null); // { id, nombre, minutos_gratis, precio_por_hora }
  const [agregandoCategoria, setAgregandoCategoria] = useState(false);

  // ── Planes ────────────────────────────────────────────────────────────────
  const [planes, setPlanes] = useState([]);
  const [agregandoPlan, setAgregandoPlan] = useState(false);
  const [editandoPlan, setEditandoPlan] = useState(null);
  const [nuevoPlan, setNuevoPlan] = useState(PLAN_VACIO);

  useEffect(() => {
    const cargarConfiguracion = async () => {
      if (!open) return;

      try {
        setLoading(true);
        const res = await apiClient.get("api/configuracion/cobros/parametros");
        if (res?.data?.success) {
          const minutosGratis =
            Number(res?.data?.data?.free_initial_time?.valor) || 0;

          setConfig((prev) => ({
            ...prev,
            tiempoGratisHabilitado:
              res?.data?.data?.is_free_time_enabled?.valor === "1"
                ? true
                : false,
            precioPorHora:
              Number(res?.data?.data?.price_per_hour?.valor) ||
              prev.precioPorHora,
            typePriceCalc:
              res?.data?.data?.type_price_calc?.valor === "fixed"
                ? "fixed"
                : "hourly",
            fixedPrice:
              Number(res?.data?.data?.fixed_price?.valor) || prev.fixedPrice,
            lostTicketValue:
              Number(res?.data?.data?.lost_ticket_value?.valor) ||
              prev.lostTicketValue,
            invoiceDiscountHours:
              Number(res?.data?.data?.invoice_discount_hours?.valor) ||
              prev.invoiceDiscountHours,
            minutosGratis: minutosGratis,
            minutosSalida:
              Number(res?.data?.data?.exit_grace_minutes?.valor) ||
              prev.minutosSalida,
            totalEspacios:
              Number(res?.data?.data?.total_parking_space?.valor) ||
              prev.totalEspacios,
            espacioParqueoUsado:
              Number(res?.data?.data?.parking_space_used?.valor) ||
              prev.espacioParqueoUsado,
          }));
          setCambiosPendientes(false);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error al cargar configuración:", error);
        showSnackbar("Error al cargar la configuración", "error");
      }
    };

    cargarConfiguracion();
  }, [open]);

  useEffect(() => {
    const cargarCategorias = async () => {
      if (!open) return;
      try {
        const res = await apiClient.get("/api/configuracion/categorias-tiempo-gratis");
        if (res?.data?.success) setCategorias(res.data.data);
      } catch (e) {
        console.error("Error cargando categorías:", e);
      }
    };
    cargarCategorias();
  }, [open]);

  useEffect(() => {
    const cargarPlanes = async () => {
      if (!open) return;
      try {
        const res = await apiClient.get("/api/planes");
        if (res?.data?.success) setPlanes(res.data.data);
      } catch (e) {
        console.error("Error cargando planes:", e);
      }
    };
    cargarPlanes();
  }, [open]);

  const handleCrearPlan = async () => {
    if (!nuevoPlan.nombre.trim()) return;
    if (nuevoPlan.tipo === "tarjeta" && !(nuevoPlan.horas_prepago > 0)) {
      showSnackbar("Debe indicar las horas asignadas para la tarjeta prepago", "error");
      return;
    }
    try {
      const res = await apiClient.post("/api/planes", nuevoPlan);
      if (res?.data?.success) {
        const planCreado = res.data.data;
        setPlanes((prev) => [...prev, planCreado]);
        setNuevoPlan(PLAN_VACIO);
        setAgregandoPlan(false);
        showSnackbar("Plan creado", "success");
      }
    } catch (e) {
      showSnackbar("Error al crear plan", "error");
    }
  };

  const handleActualizarPlan = async () => {
    if (!editandoPlan?.nombre?.trim()) return;
    try {
      const { id, ...datos } = editandoPlan;
      const res = await apiClient.put(`/api/planes/${id}`, datos);
      if (res?.data?.success) {
        setPlanes((prev) => prev.map((p) => (p.id === id ? res.data.data : p)));
        setEditandoPlan(null);
        showSnackbar("Plan actualizado", "success");
      }
    } catch (e) {
      showSnackbar("Error al actualizar plan", "error");
    }
  };

  const handleToggleActivoPlan = async (plan) => {
    try {
      const res = await apiClient.put(`/api/planes/${plan.id}`, { activo: !plan.activo });
      if (res?.data?.success) {
        setPlanes((prev) => prev.map((p) => (p.id === plan.id ? res.data.data : p)));
        showSnackbar(`Plan ${!plan.activo ? "activado" : "desactivado"}`, "success");
      }
    } catch (e) {
      showSnackbar("Error al cambiar estado del plan", "error");
    }
  };

  const handleEliminarPlan = async (id) => {
    try {
      await apiClient.delete(`/api/planes/${id}`);
      setPlanes((prev) => prev.filter((p) => p.id !== id));
      showSnackbar("Plan eliminado", "success");
    } catch (e) {
      showSnackbar(
        e?.response?.data?.error || "No se puede eliminar (tiene suscripciones activas)",
        "error"
      );
    }
  };

  const handleCrearCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) return;
    try {
      const res = await apiClient.post("/api/configuracion/categorias-tiempo-gratis", nuevaCategoria);
      if (res?.data?.success) {
        setCategorias((prev) => [...prev, res.data.data]);
        setNuevaCategoria({ nombre: "", minutos_gratis: 0 });
        setAgregandoCategoria(false);
        showSnackbar("Categoría creada", "success");
      }
    } catch (e) {
      showSnackbar("Error al crear categoría", "error");
    }
  };

  const handleActualizarCategoria = async () => {
    if (!editandoCategoria?.nombre?.trim()) return;
    try {
      const res = await apiClient.put(
        `/api/configuracion/categorias-tiempo-gratis/${editandoCategoria.id}`,
        {
          nombre: editandoCategoria.nombre,
          minutos_gratis: editandoCategoria.minutos_gratis,
          precio_por_hora: editandoCategoria.precio_por_hora ?? null,
        }
      );
      if (res?.data?.success) {
        setCategorias((prev) => prev.map((c) => (c.id === editandoCategoria.id ? res.data.data : c)));
        setEditandoCategoria(null);
        showSnackbar("Categoría actualizada", "success");
      }
    } catch (e) {
      showSnackbar("Error al actualizar categoría", "error");
    }
  };

  const handleEliminarCategoria = async (id) => {
    try {
      await apiClient.delete(`/api/configuracion/categorias-tiempo-gratis/${id}`);
      setCategorias((prev) => prev.filter((c) => c.id !== id));
      showSnackbar("Categoría eliminada", "success");
    } catch (e) {
      showSnackbar("Error al eliminar categoría", "error");
    }
  };

  const handleChange = (field, value) => {
    if (typeof value === "number" && value < 0) {
      return;
    }
    const finalValue = value;
    setConfig((prev) => ({
      ...prev,
      [field]: finalValue,
    }));
    setCambiosPendientes(true);
  };

  const handleGuardar = () => {
    setConfirmGuardar(true);
  };

  const guardar = async () => {
    if (config.totalEspacios < vehicles?.length) {
      setConfirmGuardar(false);
      showSnackbar(
        `No puedes reducir los espacios a ${config.totalEspacios}. Hay ${vehicles?.length} vehículo(s) dentro actualmente.`,
        "error",
      );
      return;
    }

    try {
      const req = {
        price_per_hour: config.precioPorHora,
        type_price_calc: config.typePriceCalc,
        fixed_price: config.fixedPrice,
        lost_ticket_value: config.lostTicketValue,
        invoice_discount_hours: config.invoiceDiscountHours,
        free_initial_time: config.minutosGratis,
        is_free_time_enabled: config.tiempoGratisHabilitado ? "1" : "0",
        exit_grace_minutes: config.minutosSalida,
        total_parking_space: config.totalEspacios,
        parking_space_used: config.espacioParqueoUsado,
      };
      await apiClient.put("/api/configuracion/cobros/parametros", req);

      handleClose();
      showSnackbar("Configuración guardada exitosamente", "success");
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      setConfirmGuardar(false);
      showSnackbar("Error al guardar la configuración", "error");
    }
  };

  const handleClose = () => {
    setCambiosPendientes(false);
    setConfirmGuardar(false);
    onClose();
  };


  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: "#495057",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Settings sx={{ fontSize: 24 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Configuración de parqueadero
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Administra las tarifas de cobro, tiempos gratuitos y la
                disponibilidad de espacios del parqueadero.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {cambiosPendientes && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tienes cambios sin guardar
            </Alert>
          )}
          {loading ? 
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} color="inherit" />
              <Typography>Cargando información...</Typography>
            </Box> : 
            <Grid container spacing={3}>
            {/* Card 1: Tarifas */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tarifas de Parqueo
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="tipo-cobro-label">Tipo de Cobro</InputLabel>
                        <Select
                          labelId="tipo-cobro-label"
                          label="Tipo de Cobro"
                          value={config.typePriceCalc}
                          onChange={(e) =>
                            handleChange("typePriceCalc", e.target.value)
                          }
                        >
                          <MenuItem value="hourly">Calculado por horas</MenuItem>
                          <MenuItem value="fixed">Valor fijo</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Precio por Hora"
                        type="number"
                        value={config.precioPorHora}
                        onChange={(e) =>
                          handleChange("precioPorHora", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {config.moneda}
                            </InputAdornment>
                          ),
                        }}
                        helperText="Valor a cobrar por cada hora de parqueo"
                        disabled={config.typePriceCalc === "fixed"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Valor Fijo"
                        type="number"
                        value={config.fixedPrice}
                        onChange={(e) =>
                          handleChange("fixedPrice", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {config.moneda}
                            </InputAdornment>
                          ),
                        }}
                        helperText="Cobro fijo independientemente de las horas"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Valor ticket perdido"
                        type="number"
                        value={config.lostTicketValue}
                        onChange={(e) =>
                          handleChange("lostTicketValue", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {config.moneda}
                            </InputAdornment>
                          ),
                        }}
                        helperText="Valor a cobrar cuando un ticket se perdió"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Horas descuento por factura"
                        type="number"
                        value={config.invoiceDiscountHours}
                        onChange={(e) =>
                          handleChange("invoiceDiscountHours", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 0.5 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Schedule fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              horas
                            </InputAdornment>
                          ),
                        }}
                        helperText="Horas a descontar por cada factura validada"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2: Espacios y Tiempos — 3 campos en una sola card */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Espacios y Tiempos
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Total de espacios"
                        type="number"
                        value={config.totalEspacios}
                        onChange={(e) =>
                          handleChange("totalEspacios", Number(e.target.value))
                        }
                        inputProps={{ min: vehicles?.length, step: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DriveEta fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              espacios
                            </InputAdornment>
                          ),
                        }}
                        helperText="Número total de espacios disponibles"
                        error={config.totalEspacios < vehicles?.length}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Espacios utilizados"
                        type="number"
                        value={config.espacioParqueoUsado}
                        onChange={(e) =>
                          handleChange("espacioParqueoUsado", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DriveEta fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              vehículos
                            </InputAdornment>
                          ),
                        }}
                        helperText={`Vehículos dentro del sistema: ${vehicles?.length}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Gracia de salida"
                        type="number"
                        value={config.minutosSalida}
                        onChange={(e) =>
                          handleChange("minutosSalida", Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Schedule fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              minutos
                            </InputAdornment>
                          ),
                        }}
                        helperText="Minutos de gracia permitidos al salir"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="h6">Tiempo Inicial Gratis</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.tiempoGratisHabilitado}
                          onChange={(e) =>
                            handleChange(
                              "tiempoGratisHabilitado",
                              e.target.checked,
                            )
                          }
                          color="primary"
                        />
                      }
                      label={
                        config.tiempoGratisHabilitado
                          ? "Habilitado"
                          : "Deshabilitado"
                      }
                    />
                  </Box>

                  {config.tiempoGratisHabilitado && (
                    <>
                    <TextField
                      fullWidth
                      label="Por defecto (minutos gratis)"
                      type="number"
                      value={config.minutosGratis}
                      onChange={(e) =>
                        handleChange("minutosGratis", Number(e.target.value))
                      }
                      inputProps={{ min: 0, step: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Schedule fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            minutos
                          </InputAdornment>
                        ),
                      }}
                      helperText="Tiempo gratis aplicado por defecto antes de empezar a cobrar"
                    />

                    <Divider sx={{ my: 2 }} />

                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Categorías adicionales</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Al cobrar podrás elegir una categoría (ej: GYM, Local) para aplicar su tiempo gratis
                      </Typography>
                    </Box>
                    <Tooltip title="Agregar categoría">
                      <IconButton color="primary" onClick={() => setAgregandoCategoria(true)}>
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell align="center"><strong>Minutos Gratis</strong></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Deja vacío para usar la tarifa global. Si se llena, esta categoría tendrá su propia tarifa por hora.">
                            <strong>Tarifa/hora (opcional)</strong>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right"><strong>Acciones</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categorias.length === 0 && !agregandoCategoria && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              No hay categorías. Haz clic en + para agregar.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {categorias.map((cat) => (
                        <TableRow key={cat.id}>
                          {editandoCategoria?.id === cat.id ? (
                            <>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={editandoCategoria.nombre}
                                  onChange={(e) => setEditandoCategoria({ ...editandoCategoria, nombre: e.target.value })}
                                  autoFocus
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0 }}
                                  value={editandoCategoria.minutos_gratis}
                                  onChange={(e) => setEditandoCategoria({ ...editandoCategoria, minutos_gratis: Number(e.target.value) })}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder="Global"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  value={editandoCategoria.precio_por_hora ?? ""}
                                  onChange={(e) => setEditandoCategoria({
                                    ...editandoCategoria,
                                    precio_por_hora: e.target.value === "" ? null : Number(e.target.value)
                                  })}
                                  InputProps={{ startAdornment: <span style={{ marginRight: 2 }}>$</span> }}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={handleActualizarCategoria}>Guardar</Button>
                                <Button size="small" onClick={() => setEditandoCategoria(null)}>Cancelar</Button>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{cat.nombre}</TableCell>
                              <TableCell align="center">{cat.minutos_gratis} min</TableCell>
                              <TableCell align="center">
                                {cat.precio_por_hora != null
                                  ? <Chip label={`$${Number(cat.precio_por_hora).toFixed(2)}/h`} size="small" color="warning" variant="outlined" />
                                  : <Typography variant="caption" color="text.disabled">Global</Typography>
                                }
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Editar">
                                  <IconButton size="small" onClick={() => setEditandoCategoria({ ...cat })}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" color="error" onClick={() => handleEliminarCategoria(cat.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                      {agregandoCategoria && (
                        <TableRow>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="Ej: Iglesia"
                              value={nuevaCategoria.nombre}
                              onChange={(e) => setNuevaCategoria({ ...nuevaCategoria, nombre: e.target.value })}
                              autoFocus
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0 }}
                              value={nuevaCategoria.minutos_gratis}
                              onChange={(e) => setNuevaCategoria({ ...nuevaCategoria, minutos_gratis: Number(e.target.value) })}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Global"
                              inputProps={{ min: 0, step: 0.01 }}
                              value={nuevaCategoria.precio_por_hora ?? ""}
                              onChange={(e) => setNuevaCategoria({
                                ...nuevaCategoria,
                                precio_por_hora: e.target.value === "" ? null : Number(e.target.value)
                              })}
                              InputProps={{ startAdornment: <span style={{ marginRight: 2 }}>$</span> }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={handleCrearCategoria}>Guardar</Button>
                            <Button size="small" onClick={() => { setAgregandoCategoria(false); setNuevaCategoria({ nombre: "", minutos_gratis: 0, precio_por_hora: null }); }}>Cancelar</Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card: Planes de parqueadero */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CardMembership color="primary" />
                      <Box>
                        <Typography variant="h6">Planes de Parqueadero</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gestiona los planes diarios y mensuales disponibles
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title="Agregar plan">
                      <IconButton color="primary" onClick={() => { setAgregandoPlan(true); setEditandoPlan(null); }}>
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Formulario nuevo plan */}
                  {agregandoPlan && (
                    <Box sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" mb={1}>Nuevo plan</Typography>
                      <PlanForm plan={nuevoPlan} onChange={setNuevoPlan} />
                      <Box display="flex" gap={1} mt={2} justifyContent="flex-end">
                        <Button size="small" onClick={() => { setAgregandoPlan(false); setNuevoPlan(PLAN_VACIO); }}>
                          Cancelar
                        </Button>
                        <Button size="small" variant="contained" onClick={handleCrearPlan}>
                          Guardar plan
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Plan</strong></TableCell>
                        <TableCell><strong>Tipo</strong></TableCell>
                        <TableCell><strong>Precio</strong></TableCell>
                        <TableCell><strong>Días / Horario</strong></TableCell>
                        <TableCell align="center"><strong>Estado</strong></TableCell>
                        <TableCell align="right"><strong>Acciones</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planes.length === 0 && !agregandoPlan && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              No hay planes. Haz clic en + para agregar.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {planes.map((plan) => (
                        <>
                          <TableRow key={plan.id} sx={{ opacity: plan.activo ? 1 : 0.5 }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{plan.nombre}</Typography>
                              {plan.descripcion && (
                                <Typography variant="caption" color="text.secondary">{plan.descripcion}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={plan.tipo === "mensual" ? "Mensual" : plan.tipo === "tarjeta" ? "Tarjeta Prepago" : "Diario"}
                                size="small"
                                color={plan.tipo === "mensual" ? "primary" : plan.tipo === "tarjeta" ? "secondary" : "default"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {plan.tipo === "tarjeta" ? (
                                <Typography variant="body2" color="secondary.main" fontWeight="bold">
                                  {plan.horas_prepago || "—"}h prepago
                                </Typography>
                              ) : (
                                <>
                                  <Typography variant="body2">${plan.precio?.toFixed(2)}</Typography>
                                  {plan.aplica_iva && (
                                    <Typography variant="caption" color="text.secondary">
                                      +IVA: ${plan.precio_con_iva?.toFixed(2)}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.3, flexWrap: "wrap", mb: 0.5 }}>
                                {DIAS.map((d) => (
                                  <Tooltip key={d.value} title={d.title}>
                                    <Chip
                                      label={d.label} size="small"
                                      color={plan.dias_semana?.includes(d.value) ? "primary" : "default"}
                                      variant={plan.dias_semana?.includes(d.value) ? "filled" : "outlined"}
                                      sx={{ fontSize: 10, height: 20, cursor: "default" }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {plan.todo_el_dia ? "Todo el día" : `${plan.hora_inicio} – ${plan.hora_fin}`}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={plan.activo ? "Desactivar" : "Activar"}>
                                <IconButton size="small" onClick={() => handleToggleActivoPlan(plan)}>
                                  {plan.activo
                                    ? <ToggleOn fontSize="small" color="success" />
                                    : <ToggleOff fontSize="small" color="disabled" />
                                  }
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => { setEditandoPlan({ ...plan }); setAgregandoPlan(false); }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" color="error" onClick={() => handleEliminarPlan(plan.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          {/* Fila de edición inline */}
                          {editandoPlan?.id === plan.id && (
                            <TableRow key={`edit-${plan.id}`}>
                              <TableCell colSpan={6} sx={{ backgroundColor: "action.hover" }}>
                                <Box p={1}>
                                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>Editando: {plan.nombre}</Typography>
                                  <PlanForm plan={editandoPlan} onChange={setEditandoPlan} />
                                  <Box display="flex" gap={1} mt={2} justifyContent="flex-end">
                                    <Button size="small" onClick={() => setEditandoPlan(null)}>Cancelar</Button>
                                    <Button size="small" variant="contained" onClick={handleActualizarPlan}>Guardar</Button>
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
          }

        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            startIcon={<Save />}
            disabled={!cambiosPendientes}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmGuardar}
        onClose={() => setConfirmGuardar(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            <Typography variant="h6">Confirmar Guardado</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de guardar la configuración?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGuardar(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={guardar} variant="contained" color="warning">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentSettings;
